import React, { ReactElement } from 'react';
import DOMPurify from 'dompurify';
import { LocalDate } from '@js-joda/core';
import { useFieldArray } from 'react-hook-form';

import { AdamedExpertConsent } from '@dreryk/egabinet-adamed-expert-frontend-api';
import { PractitionerDetails } from '@dreryk/egabinet-personnel-frontend-api';
import { PhoneNumber } from '@dreryk/phone-number-common-api';

import {
   createPasswordRule,
   createPasswordValidation,
   createTranslatedText,
   FormContextValues,
   useFormController,
   Validate,
} from '@dreryk/react-common';
import {
   appendTestableElementAttributes,
   Checkbox,
   DatePicker,
   EmailAddressInput,
   FormController,
   Link,
   LinkTarget,
   MedicalLicenceNumberInput,
   PasswordInput,
   PhoneNumberInput,
   StyledFlex,
   StyledMargin,
   StyledMaxWidth,
} from '@dreryk/react-components';

import { translationKeys } from './translations';
import { StyledAdamedExpertReadOnlySection } from './AdamedExpertRegisterForm.styled';

type ConsentCheckboxValue = Pick<AdamedExpertConsent, 'id'> & {
   selected: boolean;
};

export type AdamedExpertRegisterFormBean = {
   password: string | null;
   birthDate: LocalDate | null;
   phoneNumber: PhoneNumber | null;
   rememberMe: boolean;
   consents: ConsentCheckboxValue[];
};

export type AdamedExpertRegisterFormRendererProps = Pick<
   FormContextValues<AdamedExpertRegisterFormBean>,
   'handleSubmit' | 'formState'
>;

type AdamedExpertRegisterFormProps = Pick<
   PractitionerDetails,
   'emailAddress' | 'medicalLicenceNumber' | 'phoneNumber'
> & {
   consents: AdamedExpertConsent[];
   children: (rendererProps: AdamedExpertRegisterFormRendererProps) => ReactElement;
};

const PASSWORD_MIN_LENGTH = 8;
const adamedExpertPasswordValidation = createPasswordValidation(
   createPasswordRule.length(PASSWORD_MIN_LENGTH),
   createPasswordRule.smallLetter(),
   createPasswordRule.bigLetter(),
   createPasswordRule.number(),
   createPasswordRule.specialCharacter()
);

const validatePassword: Validate = (data) => {
   if (!adamedExpertPasswordValidation(data as string)) {
      return translationKeys.modal.passwordNotMatchingRestrictions;
   }
   return undefined;
};

// eslint-disable-next-line max-lines-per-function
export const AdamedExpertRegisterForm = (props: AdamedExpertRegisterFormProps): ReactElement => {
   const { register, handleSubmit, control, formState } = useFormController<AdamedExpertRegisterFormBean>({
      rememberMe: true,
      consents: props.consents,
   });
   const { fields } = useFieldArray({
      control: control,
      name: 'consents',
      keyName: 'id',
   });
   return (
      <StyledFlex direction={'column'} gap={24}>
         <StyledAdamedExpertReadOnlySection>
            <EmailAddressInput
               testId='emailAddress'
               label={translationKeys.modal.emailAddress}
               defaultValue={props.emailAddress}
               controller={register('emailAddress', {
                  required: true,
               })}
               disabled
            />
            <StyledMaxWidth maxWidth={100}>
               <MedicalLicenceNumberInput
                  testId='medicalLicenceNumber'
                  label={translationKeys.modal.register.medicalLicenceNumber}
                  defaultValue={props.medicalLicenceNumber}
                  controller={register('medicalLicenceNumber', {
                     required: true,
                  })}
                  disabled
               />
            </StyledMaxWidth>
         </StyledAdamedExpertReadOnlySection>
         <PasswordInput
            testId='password'
            label={translationKeys.modal.password}
            placeholder={translationKeys.modal.passwordPlaceholder}
            controller={register('password', {
               required: true,
               validate: validatePassword,
            })}
         />
         <DatePicker
            testId={'birthDate'}
            label={translationKeys.modal.register.birthDate}
            controller={register('birthDate', {
               required: true,
            })}
            dayValidators={'dayAfterToday'}
            withNavigationArrows
         />
         <PhoneNumberInput
            testId={'phoneNumber'}
            label={translationKeys.modal.register.phoneNumber}
            controller={register('phoneNumber')}
         />
         <Checkbox testId={'rememberMe'} label={translationKeys.modal.rememberMe} controller={register('rememberMe')} />
         <StyledFlex direction={'column'} gap={12} {...appendTestableElementAttributes('consentsSection')}>
            {fields.map((field, index) => (
               <StyledFlex
                  direction={'column'}
                  gap={4}
                  key={field.id}
                  {...appendTestableElementAttributes(`consentSection${index}`)}
               >
                  <Checkbox
                     key={field.id}
                     testId={`consent${index}`}
                     label={createTranslatedText(DOMPurify.sanitize(props.consents[index].content))}
                     controller={register(`consents.${index}.selected`, { required: field.required })}
                  />
                  <FormController controller={register(`consents.${index}.id`)} />
                  {props.consents[index].links.length > 0 ? (
                     <StyledFlex direction={'column'} gap={2}>
                        {props.consents[index].links.map((link, index) => (
                           <StyledMargin key={link.id} left={26}>
                              <Link
                                 testId={`consentLink${index}`}
                                 label={createTranslatedText(` • ${DOMPurify.sanitize(link.name)}`)}
                                 href={link.url}
                                 target={LinkTarget.BLANK}
                              />
                           </StyledMargin>
                        ))}
                     </StyledFlex>
                  ) : null}
               </StyledFlex>
            ))}
         </StyledFlex>
         {props.children({ handleSubmit: handleSubmit, formState: formState })}
      </StyledFlex>
   );
};
